"use client";

import { useEffect, useState } from "react";
import { api } from "./api";
import { useRegion } from "./regionContext";

/**
 * EPIC-029 STORY-1805 — fx-rate hook for the display layer.
 *
 * Returns 1 USDC = N quote, where quote is the source currency the
 * caller wants to convert FROM. Caller divides cents by `rate` to get
 * the USDC equivalent, then formats with whichever target currency
 * the user prefers.
 *
 * Caching:
 *   - In-process per pageload (60s freshness on the backend's own
 *     cache, so a tab refresh that re-fetches is at most 1 upstream
 *     call per minute per pod).
 *   - Hook-level: returns the last known rate while a refresh is
 *     in-flight (no flash-of-zero on the dashboard tiles).
 *
 * If the network call fails (rare — backend has a noop fallback),
 * returns null. Callers should handle null by either:
 *   (a) suppressing the conversion + rendering in source currency
 *       (correct for "show R$ when fx is unavailable"), OR
 *   (b) showing a small "fx unavailable" affordance.
 *
 * Closed-beta scope: BRL is the only fiat that today's positions are
 * denominated in, so this hook is almost always called with
 * `quote="BRL"`. The signature accepts other quotes for forward-
 * compatibility with multi-fiat markets (post-Gate-A).
 */
export type SupportedFxQuote =
  | "USD"
  | "BRL"
  | "EUR"
  | "GBP"
  | "CAD"
  | "MXN"
  | "INR";

interface FxRate {
  rate: number;
  fetchedAt: string;
  source: string;
}

/**
 * Region-gate map per CLAUDE.md "Default-experience and jurisdiction-
 * scoping rules" (2026-05-03 + 2026-05-08): each local-fiat projection
 * is gated to ONE matching region. Non-matching users see only the
 * USDC-as-truth canonical unit; no foreign-currency projection leaks
 * cross-region.
 *
 * Extended 2026-05-11 founder sweep — original fix only gated BRL.
 * Founder asked "could you also apply on other regions?" — so MXN
 * (Mexico), CAD (Canada), INR (future India) get the same guard.
 *
 * USD + EUR + GBP are unscoped — they're either universal reserve
 * units (USD ≈ USDC) or future-region placeholders (EUR/GBP have no
 * region assignment yet; gating them prematurely would surprise
 * callers when the matching region launches).
 */
const QUOTE_REGION_GATE: Partial<Record<SupportedFxQuote, string>> = {
  BRL: "brazil",
  MXN: "mexico",
  CAD: "canada",
  // STORY-1717b Part 2 (2026-05-11) — INR enabled. Composite chain now
  // covers it via Circle USDC/INR + Coingecko fallback + 7-day legacy
  // cache. Frontend gate is conservative: non-IN viewers see no ₹
  // projection. Activate region='india' in `regions` once
  // STORY-1724 (Razorpay/Cashfree) opens deposits.
  INR: "india",
};

export function useFxRate(quote: SupportedFxQuote): FxRate | null {
  const region = useRegion();
  // 2026-05-11 — non-matching region must produce null rate so every
  // dual-display helper that calls into this hook (formatMoneyPrimary,
  // formatBalanceForRegion, etc.) renders USDC-only without a foreign-
  // currency parenthetical. 16+ callsites inherit the fix.
  //
  // Original fix only suppressed BRL × non-brazil (founder report on
  // close-position modal); this sweep covers MXN × non-mexico, CAD ×
  // non-canada same way. Future INR follows.
  const requiredRegion = QUOTE_REGION_GATE[quote];
  const suppress = requiredRegion !== undefined && region.region !== requiredRegion;

  const [state, setState] = useState<FxRate | null>(null);

  useEffect(() => {
    if (suppress) {
      setState(null);
      return;
    }
    let cancelled = false;
    void api
      .getFxRate(quote)
      .then((r) => {
        if (!cancelled) {
          setState({
            rate: r.rate,
            fetchedAt: r.fetchedAt,
            source: r.source,
          });
        }
      })
      .catch(() => {
        // Network/backend hiccup — leave state at last known (or null
        // on first load). Caller decides whether to render with
        // source-currency fallback or hide the value.
      });
    return () => {
      cancelled = true;
    };
  }, [quote, suppress]);

  // Final guard: even if a stale `state` slipped through from a prior
  // matching-region render before the effect re-ran, never return a
  // gated rate outside its required region.
  if (suppress) return null;
  return state;
}
